/**
 * 通用文件上传组件
 * 支持图片(picture-card)和文件(text)两种展示模式
 */
import React, { useState } from 'react';
import { Upload, message, Image } from 'antd';
import { PlusOutlined, InboxOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile, RcFile } from 'antd/es/upload';
import { uploadAPI, adminUploadAPI } from '../services/api';
import { formatErrorMessage } from '../utils/errorHandler';

export type FileCategory = 'image' | 'pk_data' | 'model_code' | 'attachment';

interface FileUploaderProps {
  category: FileCategory;
  value?: string[];
  onChange?: (urls: string[]) => void;
  maxCount?: number;
  disabled?: boolean;
  useAdminAPI?: boolean; // 新增：是否使用 admin API
}

const ACCEPT_MAP: Record<FileCategory, string> = {
  image: '.png,.jpg,.jpeg,.gif,.webp,.svg',
  pk_data: '.csv,.xlsx,.xls',
  model_code: '.R,.r,.ctl,.mod,.mlx,.phnx,.cpp,.txt',
  attachment: '.pdf,.doc,.docx,.txt,.zip,.png,.jpg,.jpeg,.csv,.xlsx,.xls,.r,.ctl,.mod',
};

const API_BASE = process.env.REACT_APP_API_URL || '';

const FileUploader: React.FC<FileUploaderProps> = ({
  category,
  value = [],
  onChange,
  maxCount = 5,
  disabled = false,
  useAdminAPI = false, // 默认使用用户 API
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const isImageMode = category === 'image';

  // Convert URL list to Upload fileList format
  const fileList: UploadFile[] = value.map((url, idx) => ({
    uid: `${idx}-${url}`,
    name: url.split('/').pop() || 'file',
    status: 'done' as const,
    url: url.startsWith('http') ? url : `${API_BASE}${url}`,
    thumbUrl: isImageMode ? (url.startsWith('http') ? url : `${API_BASE}${url}`) : undefined,
  }));

  const handleUpload = async (options: any) => {
    const { file, onSuccess, onError } = options;
    try {
      // 根据 useAdminAPI 选择使用哪个 API
      const api = useAdminAPI ? adminUploadAPI : uploadAPI;
      const res = await api.upload(file as File, category);
      onSuccess(res, file);
      const newUrls = [...value, res.url];
      onChange?.(newUrls);
    } catch (err: any) {
      message.error(formatErrorMessage(err, '上传失败'));
      onError(err);
    }
  };

  const handleRemove = (file: UploadFile) => {
    // Find the URL matching this file and remove it
    const fileUrl = file.url || '';
    const relativePath = fileUrl.replace(API_BASE, '');
    const newUrls = value.filter(u => u !== relativePath && u !== fileUrl);
    onChange?.(newUrls);
    return true;
  };

  const handlePreview = async (file: UploadFile) => {
    if (isImageMode) {
      setPreviewImage(file.url || file.thumbUrl || '');
      setPreviewOpen(true);
    } else if (file.url) {
      window.open(file.url, '_blank');
    }
  };

  const uploadButton = isImageMode ? (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 4, fontSize: 12 }}>上传图片</div>
    </div>
  ) : (
    <div>
      <UploadOutlined />
      <span style={{ marginLeft: 4 }}>上传文件</span>
    </div>
  );

  return (
    <>
      <Upload
        customRequest={handleUpload}
        fileList={fileList}
        onRemove={handleRemove}
        onPreview={handlePreview}
        listType={isImageMode ? 'picture-card' : 'text'}
        accept={ACCEPT_MAP[category]}
        maxCount={maxCount}
        disabled={disabled}
        multiple={maxCount > 1}
      >
        {fileList.length >= maxCount ? null : uploadButton}
      </Upload>
      {isImageMode && previewImage && (
        <Image
          wrapperStyle={{ display: 'none' }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
          }}
          src={previewImage}
        />
      )}
    </>
  );
};

export default FileUploader;
